import { isMobileSafari, isSafari } from 'react-device-detect'
import dayjs from 'dayjs'

/**
 * Converts blob file into base64 string
 *
 * @param {Blob} file
 * @returns {Promise<string>} base64 string
 */
export const toBase64 = (file) => {
  if (typeof FileReader === 'undefined') {
    return Promise.reject(new Error('Your browser does not support FileReader API'))
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    // `onerror` fires with a ProgressEvent, not an Error. Rejecting with it raw produces an
    // unactionable `{"isTrusted":true}` unhandled rejection in Honeybadger, so wrap it in a
    // real Error that carries the underlying reason.
    reader.onerror = () => reject(new Error(`Failed to read file: ${reader.error?.message ?? 'unknown error'}`))
  })
}

export const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/tiff': ['.tiff'],
  'application/pdf': ['.pdf'],
}

export const isImage = (mimetype) => Boolean(mimetype?.startsWith('image/'))

export const isPdf = (mimetype) => mimetype === 'application/pdf'

export const getFileId = (file) => `${file.name}-${file.size}-${file.lastModified || ''}`

/**
 * Opens blob file in new tab
 *
 * @param blob - blob file
 * @param filename - file name
 */
export function viewBlob(blob, filename = `file ${dayjs().format('L LT')}`) {
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    const blobUrl = createObjectURL(blob)

    if (!isSafari && !isMobileSafari) {
      window.open(blobUrl)
      return
    }

    // download file instead if it is safari

    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobUrl
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink)
      window.URL.revokeObjectURL(blobUrl)
    }, 200)
  }
}

/**
 * Format bytes to human-readable format
 */
export const prettyBytes = (bytes) => {
  if (isNaN(bytes)) {
    throw new TypeError('Expected a number')
  }

  const exponent = Math.floor(Math.log(bytes) / Math.log(1000))
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const n = Number(bytes / Math.pow(1000, exponent)).toPrecision(3)
  return n + ' ' + units[exponent]
}

/**
 * Download file from browser
 *
 * @example
 * api.get(`/export`, { responseType: 'blob' }).then(() => {
 *    downloadFile(response.data, 'export.csv')
 * })
 */
export function downloadFile(data, filename = `file ${dayjs().format('L LT')}`, mime) {
  const blobData = [data]
  const blob = new Blob(blobData, { type: mime || 'application/octet-stream' })
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename)
  } else {
    const blobURL = createObjectURL(blob)
    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink)
      window.URL.revokeObjectURL(blobURL)
    }, 200)
  }
}

const createObjectURL = (blob) =>
  window.URL && window.URL.createObjectURL ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob)

export function getFileType(mimetype) {
  if (mimetype.includes('pdf')) return 'pdf'
  if (mimetype.includes('image')) return 'image'
  if (mimetype.includes('excel') || mimetype.includes('spreadsheet') || mimetype.includes('csv')) return 'excel'
  if (
    mimetype.includes('word') ||
    mimetype.includes('document') ||
    mimetype.includes('msword') ||
    mimetype.includes('officedocument.wordprocessing')
  )
    return 'word'
  return 'other'
}
